import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { trackOutbound } from "../lib/track";

const SPE_SEARCH = "https://www.stayplayexplore.co.uk/search";

const PACKAGE_OPTIONS = [
  { value: "", label: "Any Perfect Break" },
  { value: "adults-only-breaks-with-3-experiences-included", label: "The Heart of England Break" },
  { value: "taste-the-perfect-break-for-foodies", label: "Taste — Foodie Break" },
];

const FLEX_OPTIONS = [
  { value: "0", label: "Exact dates" },
  { value: "1", label: "± 1 day" },
  { value: "2", label: "± 2 days" },
  { value: "3", label: "± 3 days" },
  { value: "5", label: "± 5 days" },
  { value: "7", label: "± 7 days" },
];

// Native TPB search bar: collects dates/nights/flexibility here, then opens
// SPE's results page (same parameter grammar as SPE's own search widget) in a new tab.
export function SearchWidget({ presetPackage = "", placement = "search-widget" }) {
  const [startDate, setStartDate] = useState("");
  const [nights, setNights] = useState("1");
  const [fuzzy, setFuzzy] = useState("0");
  const [pkg, setPkg] = useState(presetPackage);
  const [adults, setAdults] = useState("2");

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (startDate) {
      params.set("startDate", startDate);
      const end = new Date(`${startDate}T12:00:00`);
      end.setDate(end.getDate() + Number(nights));
      params.set("endDate", end.toISOString().slice(0, 10));
    }
    params.set("adults", adults);
    if (fuzzy !== "0") params.set("fuzzyDays", fuzzy);
    const pack = presetPackage || pkg;
    if (pack) params.set("package", pack);
    params.set("utm_source", "theperfectbreak");
    params.set("utm_medium", "referral_site");
    params.set("utm_campaign", "tpb_site");
    params.set("utm_content", placement);
    trackOutbound({ break_slug: pack || null, destination_url: SPE_SEARCH, placement });
    window.open(`${SPE_SEARCH}?${params.toString()}`, "_blank", "noopener,noreferrer");
  };

  const field = "w-full rounded-full border border-sandline bg-sand px-4 py-3 text-sm text-ink";
  const label = "mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-ink-muted";

  return (
    <div className="rounded-lg border border-sandline bg-white p-6 shadow-md sm:p-8" data-testid={`search-widget-${placement}`}>
      <p className="overline">Start with your dates</p>
      <h2 className="mt-2 font-serif text-2xl font-semibold text-ink sm:text-3xl">When are you thinking of going?</h2>
      <form onSubmit={submit} className={`mt-6 grid gap-4 sm:grid-cols-2 ${presetPackage ? "lg:grid-cols-5" : "lg:grid-cols-6"}`} data-testid={`search-form-${placement}`}>
        <div>
          <label className={label} htmlFor={`sw-date-${placement}`}>Start date</label>
          <input
            id={`sw-date-${placement}`}
            type="date"
            className={field}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            min={new Date().toISOString().slice(0, 10)}
            data-testid={`search-date-${placement}`}
          />
        </div>
        <div>
          <label className={label} htmlFor={`sw-nights-${placement}`}>Nights</label>
          <select id={`sw-nights-${placement}`} className={field} value={nights} onChange={(e) => setNights(e.target.value)} data-testid={`search-nights-${placement}`}>
            {[1, 2, 3, 4, 5, 6, 7].map((n) => (
              <option key={n} value={n}>{`${n} night${n > 1 ? "s" : ""}`}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={label} htmlFor={`sw-flex-${placement}`}>Flexibility</label>
          <select id={`sw-flex-${placement}`} className={field} value={fuzzy} onChange={(e) => setFuzzy(e.target.value)} data-testid={`search-flex-${placement}`}>
            {FLEX_OPTIONS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
        {!presetPackage && (
          <div>
            <label className={label} htmlFor={`sw-pkg-${placement}`}>Which break?</label>
            <select id={`sw-pkg-${placement}`} className={field} value={pkg} onChange={(e) => setPkg(e.target.value)} data-testid={`search-package-${placement}`}>
              {PACKAGE_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className={label} htmlFor={`sw-adults-${placement}`}>Adults</label>
          <select id={`sw-adults-${placement}`} className={field} value={adults} onChange={(e) => setAdults(e.target.value)} data-testid={`search-adults-${placement}`}>
            {[2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>{`${n} adults`}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <button type="submit" className="btn-accent group w-full !py-3" data-testid={`search-submit-${placement}`}>
            Check dates &amp; book <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </form>
      <p className="mt-3 text-xs leading-relaxed text-ink-muted" data-testid={`search-reassurance-${placement}`}>
        Leave the date blank to browse everything. Results and secure booking are completed through our established Stay Play Explore booking system.
        {Number(adults) > 2 && " Travelling as more than two? The booking system will suggest multiple rooms — guide pricing is per room, for two."}
      </p>
    </div>
  );
}
